import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Plus, Trash2, Search, Palette, X, Save, Edit3 } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";

const EMPTY = { color: "", price_per_m2: 0, manufacturer: "", effect: "", notes: "" };
export const EFFECTS = ["Efekty betonu", "Efekty perleťové", "Efekty metalické", "Efekty rzi", "Efekty mramoru", "Efekty brokátové"];
const UNASSIGNED = "__none__";

export default function PriceListCoatings() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [effectTab, setEffectTab] = useState("");
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = () => api.get("/coatings").then((r) => { setItems(r.data); setLoading(false); });
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editing.color?.trim()) { toast.error("Vyplňte barvu"); return; }
        try {
            const payload = { color: editing.color, manufacturer: editing.manufacturer || "", effect: editing.effect || "", notes: editing.notes || "", price_per_m2: Number(editing.price_per_m2) || 0 };
            if (editing.id) await api.put(`/coatings/${editing.id}`, payload);
            else await api.post("/coatings", payload);
            toast.success("Uloženo");
            setEditing(null);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
    };

    const del = async (id) => {
        if (!window.confirm("Opravdu smazat tuto stěrku?")) return;
        try { await api.delete(`/coatings/${id}`); toast.success("Smazáno"); load(); }
        catch { toast.error("Smazání selhalo"); }
    };

    const filtered = useMemo(() => items.filter((c) => {
        const s = q.toLowerCase();
        if (effectTab === UNASSIGNED && c.effect) return false;
        if (effectTab && effectTab !== UNASSIGNED && c.effect !== effectTab) return false;
        return !q || (c.color + " " + (c.manufacturer || "") + " " + (c.effect || "") + " " + (c.notes || "")).toLowerCase().includes(s);
    }), [items, q, effectTab]);
    const unassignedCount = items.filter((c) => !c.effect).length;
    const countFor = (e) => items.filter((c) => c.effect === e).length;

    const avgPrice = items.length ? items.reduce((a, c) => a + (Number(c.price_per_m2) || 0), 0) / items.length : 0;
    const minPrice = items.length ? Math.min(...items.map((c) => Number(c.price_per_m2) || 0)) : 0;
    const maxPrice = items.length ? Math.max(...items.map((c) => Number(c.price_per_m2) || 0)) : 0;

    return (
        <div className="px-10 py-10 max-w-7xl mx-auto animate-fade-in" data-testid="coatings-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Ceník</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Dekorativní stěrky</h1>
                    <p className="text-sm text-slate-600 mt-2">Barvy stěrek a cena za m² (práce + materiál dohromady).</p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })} data-testid="new-coating-button"
                        className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    <Plus className="w-4 h-4" /> Nová barva
                </button>
            </div>

            {items.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-8">
                    <Stat label="Celkem barev" value={String(items.length)} testid="stat-count" />
                    <Stat label="Nejnižší" value={fmt(minPrice)} testid="stat-min" />
                    <Stat label="Průměr" value={fmt(avgPrice)} testid="stat-avg" gold />
                    <Stat label="Nejvyšší" value={fmt(maxPrice)} testid="stat-max" />
                </div>
            )}

            <div className="bg-white border border-slate-200">
                <div className="flex border-b-2 border-slate-900 overflow-x-auto no-scrollbar" data-testid="effect-tabs">
                    {[["", "Vše", items.length], ...EFFECTS.map((e) => [e, e, countFor(e)]), ...(unassignedCount ? [[UNASSIGNED, "Nezařazené", unassignedCount]] : [])].map(([val, label, cnt]) => (
                        <button key={val || "all"} onClick={() => setEffectTab(val)} data-testid={`effect-tab-${val === UNASSIGNED ? "unassigned" : val || "all"}`}
                                className={`px-4 py-3 text-[11px] uppercase tracking-widest font-bold whitespace-nowrap border-b-[3px] -mb-[2px] transition-colors ${effectTab === val ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"} ${val === UNASSIGNED ? "text-red-700" : ""}`}>
                            {label} <span className="text-slate-400 font-normal ml-1">{cnt}</span>
                        </button>
                    ))}
                </div>
                <div className="flex items-center px-4 py-3 border-b border-slate-200 gap-3">
                    <Search className="w-4 h-4 text-slate-400" />
                    <input placeholder="Hledat barvu, výrobce, efekt…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="coatings-search"
                           className="flex-1 text-sm focus:outline-none bg-transparent" />
                    <span className="text-xs text-slate-400">{filtered.length} / {items.length}</span>
                </div>

                {loading ? (
                    <div className="p-12 text-center text-slate-400 text-xs uppercase tracking-widest">Načítání…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <Palette className="w-12 h-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
                        <div className="font-display text-lg font-bold text-slate-700">{items.length === 0 ? "Žádná barva v ceníku" : "Nic nenalezeno"}</div>
                        <p className="text-sm text-slate-500 mt-1 mb-6">{items.length === 0 ? "Přidejte první barvu stěrky s cenou za m²." : "Zkuste jiné hledání."}</p>
                        {items.length === 0 && (
                            <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                                <Plus className="w-4 h-4" /> Přidat barvu
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-slate-900">
                                <th className="px-4 py-3 font-semibold">Barva</th>
                                <th className="px-4 py-3 font-semibold">Výrobce</th>
                                <th className="px-4 py-3 font-semibold">Efekt</th>
                                <th className="px-4 py-3 font-semibold">Poznámka</th>
                                <th className="px-4 py-3 font-semibold text-right">Cena / m²</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody className="tabular-data">
                            {filtered.map((c, i) => (
                                <tr key={c.id} className={`border-b border-slate-100 hover:bg-orange-50/40 ${i % 2 ? "bg-slate-50/40" : ""}`} data-testid={`coating-row-${c.id}`}>
                                    <td className="px-4 py-3 font-semibold text-slate-900">{c.color}</td>
                                    <td className="px-4 py-3 text-slate-600">{c.manufacturer || "—"}</td>
                                    <td className="px-4 py-3 text-xs uppercase tracking-wider" data-testid={`coating-effect-${c.id}`}>{c.effect ? <span className="text-slate-600">{c.effect}</span> : <span className="text-red-700 font-bold">Nezařazeno</span>}</td>
                                    <td className="px-4 py-3 text-slate-500 text-xs max-w-md">
                                        <div className="line-clamp-1">{c.notes || "—"}</div>
                                    </td>
                                    <td className="px-4 py-3 text-right font-semibold text-orange-700">{fmt(c.price_per_m2)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => setEditing({ ...c })} data-testid={`edit-coating-${c.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-sm">
                                                <Edit3 className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => del(c.id)} data-testid={`delete-coating-${c.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded-sm">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
                     data-testid="coating-modal" onClick={() => setEditing(null)}>
                    <div className="bg-white border border-slate-200 shadow-2xl max-w-lg w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                            <div>
                                <div className="label-eyebrow mb-1">{editing.id ? "Úprava" : "Nová barva"}</div>
                                <h2 className="font-display text-xl font-black tracking-tight text-slate-900">Dekorativní stěrka</h2>
                            </div>
                            <button onClick={() => setEditing(null)} data-testid="coating-modal-close" className="text-slate-400 hover:text-slate-900">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-6 py-5 grid grid-cols-2 gap-4">
                            <F label="Barva *" className="col-span-2">
                                <input value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} data-testid="coating-color-input"
                                       placeholder="Např. Antracit, Písek, Terracotta..."
                                       className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </F>
                            <F label="Cena / m² (práce + materiál)" className="col-span-2">
                                <input type="number" step="1" min="0" value={editing.price_per_m2} onChange={(e) => setEditing({ ...editing, price_per_m2: e.target.value })} data-testid="coating-price-input"
                                       className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </F>
                            <F label="Výrobce">
                                <input value={editing.manufacturer} onChange={(e) => setEditing({ ...editing, manufacturer: e.target.value })} data-testid="coating-manufacturer"
                                       placeholder="Marmorino, Stucco..."
                                       className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600" />
                            </F>
                            <F label="Efekt">
                                <select value={editing.effect || ""} onChange={(e) => setEditing({ ...editing, effect: e.target.value })} data-testid="coating-effect"
                                        className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm bg-white focus:outline-none focus:border-orange-600">
                                    <option value="">— Nezařazeno —</option>
                                    {EFFECTS.map((f) => <option key={f} value={f}>{f}</option>)}
                                </select>
                            </F>
                            <F label="Poznámka" className="col-span-2">
                                <textarea rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="coating-notes"
                                          placeholder="Doporučené použití, počet vrstev..."
                                          className="w-full border border-slate-300 px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-orange-600 resize-none" />
                            </F>
                        </div>
                        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm font-semibold uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-sm">
                                Zrušit
                            </button>
                            <button onClick={save} data-testid="coating-save-button"
                                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2 text-sm font-semibold uppercase tracking-wider rounded-sm">
                                <Save className="w-4 h-4" /> Uložit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, testid, gold }) {
    const border = gold ? "border-orange-600" : "border-slate-200";
    const valueColor = gold ? "text-orange-700" : "text-slate-900";
    return (
        <div className={`bg-white border-l-4 ${border} border-t border-r border-b border-slate-200 px-4 py-3`} data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">{label}</div>
            <div className={`font-display text-xl font-black tracking-tight ${valueColor}`}>{value}</div>
        </div>
    );
}

function F({ label, children, className = "" }) {
    return (
        <div className={className}>
            <label className="block text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">{label}</label>
            {children}
        </div>
    );
}
